import type { PatientData } from './service'

type AddressValue = PatientData['addresses'][number]

const isFilled = (address: AddressValue): boolean =>
  !!(
    address.line?.trim() ||
    address.city?.trim() ||
    address.state?.trim() ||
    address.postalCode?.trim()
  )

/**
 * Indexes of the address rows that validation applies to (PD-1425).
 *
 * Mirrors packages/patient-web/src/pages/Account/components/Profile.tsx (the
 * `values.addresses.forEach` block in its `validate`), for the same @mantine/form
 * version and the same defect:
 *  - `use: 'old'` rows are soft-deleted by the Patient App. The modal never renders
 *    them (`visibleAddressIndexes`), so validating them fails submit on inputs the
 *    user cannot see, reach or fix — the modal just does nothing.
 *  - an extra row is validated only once something has been typed into it. A blank
 *    optional row is dropped on save anyway (see the addresses `.filter` in
 *    service.ts) so it must not block submit.
 *
 * Deliberate adaptation of patient-web's `index === 0`: "required" attaches to the
 * first ACTIVE row, not to raw index 0, because in this ticket's own scenario index
 * 0 IS the soft-deleted row — keying off index 0 would leave the single visible,
 * asterisked address unvalidated and let a blank address save. First-active is also
 * what the UI already promises (`withAsterisk={visibleIndex === 0}`).
 */
export function getValidatedAddressIndexes(addresses: AddressValue[] = []): number[] {
  const firstActiveIndex = addresses.findIndex((address) => address.use !== 'old')

  return addresses.reduce<number[]>((acc, address, index) => {
    if (address.use !== 'old' && (index === firstActiveIndex || isFilled(address))) {
      acc.push(index)
    }
    return acc
  }, [])
}

/**
 * Mantine `validate` errors for the address rows, keyed `addresses.<index>.<field>`
 * so they render next to the offending input.
 *
 * The per-field rules are the pre-existing ones moved verbatim off the declarative
 * `addresses: {...}` block — only *which rows* are checked changes.
 *
 * ponytail: an empty ZIP still yields no error here, exactly as the rule it replaces
 * did; the "ZIP is required" case is owned by the explicit check in
 * CreateUpdatePatientModal's handleFormSubmit, which sets an inline
 * `addresses.<index>.postalCode` error and also raises a notification.
 * Upgrade path: fold that check in here (patient-web treats an empty ZIP as an
 * inline error) if the two are ever wanted as one inline rule.
 */
export function validateAddresses(addresses: AddressValue[] = []): Record<string, string> {
  const errors: Record<string, string> = {}

  for (const index of getValidatedAddressIndexes(addresses)) {
    const address = addresses[index]

    if ((address.line ?? '').length < 3) {
      errors[`addresses.${index}.line`] = 'Invalid address line'
    }
    if ((address.city ?? '').length < 2) {
      errors[`addresses.${index}.city`] = 'Invalid city'
    }
    if ((address.state ?? '').length < 2) {
      errors[`addresses.${index}.state`] = 'Invalid state'
    }
    if (address.postalCode) {
      const digits = address.postalCode.replace(/\D/g, '')
      if (!(digits.length === 5 || digits.length === 9)) {
        errors[`addresses.${index}.postalCode`] = 'ZIP code must be 5 or 9 digits'
      }
    }
  }

  return errors
}
